import React, { useEffect, useMemo, useState } from "react"
import { css } from "@emotion/react"
import { Redirect, useParams } from "react-router-dom"
import { Icon, PartClearancePriceEcomm, PartImage, TrackingEvent } from "@ncs/web-legos"
import {
	CustomerPart,
	isNonParentCustomerPart,
	NonParentCustomerPart,
	useOnlinePartEcommService,
	useOnlinePartDetailsEcommService,
	usePublishedAssets,
	PublishedAsset,
} from "@ncs/ncs-api"
import {
	Box,
	Button,
	Divider,
	EventTracker,
	encodeUrlState,
	Heading,
	Link,
	Paragraph,
	PDPPriceEcomm,
	useChangeCallback,
	useScrollToTop,
	calculateEcommDiscount,
	useUrlState,
} from "@ncs/web-legos"
import { useShopContext } from "~/contexts"
import {
	AddedToCartModalEcomm,
	PageContentWrapper,
	PartPreviewRow,
	QuantityControl,
} from "~/shared-components"
import { PartChildSelector } from "./components"
import { DESCRIPTION_MAX_LENGTH } from "~/constants"
import PartDetailTabs from "./components/TechnicalDescription/PartDetails"
import {
	styleGoToCart,
	stylePartContainer,
	styleAddToCardContainer,
	styleAddToCardLabel,
	styleQuantityControl,
	styleInStock,
	styleMoreInfoBtn,
	styleLabelDescription,
	styleAlternatePartNumber,
	styleAlternatePartNumbersContainer,
	alternatePartNumbersWrapper,
	partNumberWrapper,
	styleDetailsContainer,
	styleOnlinePartNumber,
	styleContainerOnlinePartNumber,
	styleBrandName,
	stylePartTitle,
	clearanceTag,
	priceContainer,
	partDetailWrapper,
	stylePartImage,
} from "./styles"
import { useAnalytics } from "~/hooks/useAnalytics"
import MultiImages from "./components/MultiImages"

type PartDetailEcommUrlState = {
	childId: string | null
}

export const PartDetail: React.FC = () => {
	const scrollToTop = useScrollToTop()
	const { partId } = useParams<{ partId: string }>()
	// The part that we're immediately pointed to via the ID in the URL.
	const [initialPart, loadingInitialPart, { query }] = useOnlinePartEcommService(partId)
	const [partDetails, loadingPartDetails] = useOnlinePartDetailsEcommService(partId)
	const [localQuantity, setLocalQuantity] = useState(1)
	const [showMoreInfo, setShowMoreInfo] = useState(false)
	const [shopState, shopDispatch] = useShopContext()
	const [showAddedToCartModal, setShowAddedToCartModal] = useState(false)

	const unitOfMeasure = partDetails?.part?.unitOfMeasure
	const { trackEvent } = useAnalytics()

	const manufacturer = (partDetails?.manufacturers ?? [])
		.map((i) => i.manufacturerName)
		.join(", ")

	const handleMoreInfo = () => {
		setShowMoreInfo((moreInfo) => !moreInfo)
		trackEvent(TrackingEvent.PDP_MORE_INFO)
	}

	const brandName = useMemo(
		() => partDetails?.brands.map((brand) => brand.brandName).join(", "),
		[partDetails]
	)
	// Store the selected child's ID.
	const [{ childId: selectedChildId }, { updateUrlValue }] =
		useUrlState<PartDetailEcommUrlState>({
			childId: null,
		})
	const [selectedPartDetails] = useOnlinePartDetailsEcommService(selectedChildId)
	// Fetch the full CustomerPart for the selected child.
	const [selectedChildPart, loadingChildPartRaw] = useOnlinePartEcommService(selectedChildId)
	// Loading state of child part should include waiting for its parent too.
	const loadingChildPart = loadingInitialPart || loadingChildPartRaw

	/** This is the actual part we'd add to the cart after figuring out if it's
	 * a parent or not, and if so, selecting the child. */
	const selectedPart: NonParentCustomerPart | null = useMemo(() => {
		if (selectedChildPart && isNonParentCustomerPart(selectedChildPart)) {
			return selectedChildPart
		}
		if (!!initialPart && !initialPart.isParent && isNonParentCustomerPart(initialPart)) {
			return initialPart
		}

		return null
	}, [initialPart, selectedChildPart])

	const [assetsToRender, setAssetsToRender] = useState<PublishedAsset[]>([])

	const [publishedAssets, loadingPublishedAssets] = usePublishedAssets(
		selectedPart?.part?.id ?? initialPart?.part?.id,
		{
			queryConfig: {
				enabled: !!(selectedPart?.part?.id ?? initialPart?.part?.id),
				retry: false,
			},
		}
	)

	useEffect(() => {
		const imageUrl = selectedPart?.imageUrl ?? initialPart?.imageUrl
		const coverAsset =
			imageUrl ?
				[
					{
						assetUrl: imageUrl,
						description: initialPart?.title || "",
						assetName: "Cover Image",
						assetRole: "IMAGE",
						assetExtensionType: "jpg",
						id: 1,
						thumbnailDisplayOrdering: "1",
						metadata: undefined,
					},
				]
			:	[]
		const rotationalAsset =
			publishedAssets?.assets?.filter((image) => image.assetRole === "360") ?? []
		const publishedAssetsList =
			publishedAssets?.assets?.filter((image) => image.assetRole !== "360") ?? []
		const assets = [...coverAsset, ...rotationalAsset, ...publishedAssetsList]
		setAssetsToRender(assets as PublishedAsset[])
	}, [publishedAssets, initialPart, selectedPart])

	const alreadyInCartTally = useMemo(() => {
		return shopState.cart.reduce(
			(
				prev: number,
				{ part: partInCart, quantity }: { part: CustomerPart; quantity: number }
			) => (partInCart.id === selectedPart?.id ? prev + quantity : prev),
			0
		)
	}, [selectedPart, shopState.cart])

	const handleAddToCart = () => {
		if (selectedPart) {
			// Add to cart.
			shopDispatch({
				type: "add part to cart",
				payload: {
					part: selectedPart,
					quantity: localQuantity,
				},
			})
			// Show the modal.
			setShowAddedToCartModal(true)
			// Reset our local quantity.
			setLocalQuantity(1)

			trackEvent(TrackingEvent.PDP_ADD_TO_CART, {
				id: selectedPart.id,
				name: selectedPart?.part?.description,
			})
		}
	}

	useEffect(() => {
		if (partDetails && partDetails.children) {
			const parts = [...partDetails.children]

			if (parts.length > 0 && !selectedChildId) {
				updateUrlValue("childId", parts[0]?.id.toString())
			}
		}
	}, [partDetails, selectedChildId, updateUrlValue])

	// Add the part to the Recent Parts list.
	useChangeCallback(initialPart, (part) => {
		if (part && !part.isChild) {
			shopDispatch({
				type: "add part to recent parts",
				payload: part.id,
			})
		}
	})

	useEffect(() => {
		scrollToTop()
	}, [partId, scrollToTop])

	// If we came to the page with the ID of a child part in the URL, we should
	// redirect to its parent.
	if (initialPart?.isChild && partDetails?.parentId) {
		return (
			<Redirect
				to={`/shop/parts/${partDetails.parentId}${encodeUrlState<PartDetailEcommUrlState>({
					childId: initialPart.id,
				})}`}
			/>
		)
	}

	const handleAddRelatedProductToCart = (id: string) => {
		trackEvent(TrackingEvent.RP_ADD_TO_CART, { id })
	}

	const handleAddRecentlyViewProductToCart = (id: string) => {
		trackEvent(TrackingEvent.RV_ADD_TO_CART, { id })
	}

	const getSlicedText = (text: string, isSliced: boolean) => {
		const textMaxlength = DESCRIPTION_MAX_LENGTH
		if (isSliced && text.length > textMaxlength) {
			return text.slice(0, textMaxlength) + "..."
		}
		return text
	}

	const recentlyViewedPartIds =
		shopState?.recentlyViewedPartIds?.filter((id) => id !== partId) ?? []
	const relatedPartIds = partDetails?.relatedParts ?? []
	const alternatePartNumbers =
		selectedPartDetails?.alternatePartNumbers ?? partDetails?.alternatePartNumbers ?? []

	return (
		<PageContentWrapper
			title={initialPart?.title}
			breadcrumbs={[
				{ name: "Shop", to: "/shop" },
				{ name: "Search", to: "/shop/search" },
				{ name: initialPart?.title ?? "" },
			]}
			showNotFound={query.status === "error"}
		>
			<div css={partDetailWrapper}>
				<div css={stylePartContainer}>
					<Box maxWidth="384px" width="100%">
						{selectedPart?.clearanceItemDiscount && (
							<Box display="flex" flexDirection="row" css={clearanceTag}>
								<Icon icon="tag" width={1.25} height={1.25} />
								<span>
									{calculateEcommDiscount(
										selectedPart?.clearanceItemDiscount,
										selectedPart?.price
									)}
									% Off
								</span>
							</Box>
						)}

						{!loadingPublishedAssets && assetsToRender.length === 0 && (
							<PartImage
								src={initialPart?.imageUrl ?? ""}
								css={stylePartImage}
								alt={initialPart?.title ?? ""}
							/>
						)}

						{assetsToRender.length > 0 && !loadingPublishedAssets && (
							<MultiImages
								assets={assetsToRender}
								isLoading={loadingPublishedAssets}
								partName={initialPart?.title ?? ""}
							/>
						)}
					</Box>

					<div css={styleDetailsContainer}>
						<Paragraph css={styleBrandName} isLoading={loadingPartDetails}>
							{brandName || manufacturer}
						</Paragraph>
						<Heading variant="h2" isLoading={loadingInitialPart} css={stylePartTitle}>
							{initialPart?.title}
						</Heading>
						<Box css={partNumberWrapper}>
							<div css={styleContainerOnlinePartNumber}>Part Number</div>
							<Paragraph isLoading={loadingChildPart} css={styleOnlinePartNumber}>
								{selectedPart?.onlinePartNumber}
							</Paragraph>
						</Box>
						{alternatePartNumbers.length > 0 && (
							<Box css={alternatePartNumbersWrapper}>
								<div
									css={css`
										min-width: 192px;
									`}
								>
									Replacement for
								</div>
								<div css={styleAlternatePartNumbersContainer}>
									{alternatePartNumbers.map((partNumber) => (
										<Paragraph
											isLoading={loadingChildPart}
											css={styleAlternatePartNumber}
											key={`alt_part_number_${partNumber}`}
										>
											{partNumber}
										</Paragraph>
									))}
								</div>
							</Box>
						)}

						<Box
							css={css`
								margin-top: 24px;
								margin-bottom: 34px;
							`}
						>
							<span css={styleLabelDescription}>Description</span>
							<Box d="flex" flexDirection="row">
								<Paragraph isLoading={loadingInitialPart}>
									{getSlicedText(initialPart?.description ?? "", !showMoreInfo)}
									{!showMoreInfo &&
										initialPart?.description &&
										initialPart?.description?.length >
											DESCRIPTION_MAX_LENGTH && (
											<Button onClick={handleMoreInfo}>
												<div css={styleMoreInfoBtn}>More Info</div>
											</Button>
										)}
								</Paragraph>
							</Box>
						</Box>

						{initialPart?.isParent && !!partDetails && (
							<PartChildSelector
								parentPart={initialPart}
								childParts={partDetails.children}
								selectedChildId={selectedChildId}
								setSelectedChildId={(newId) => updateUrlValue("childId", newId)}
								loadingPartDetails={loadingPartDetails}
							/>
						)}

						<Paragraph isLoading={loadingInitialPart} css={styleInStock}>
							In Stock
						</Paragraph>

						<QuantityControl
							value={localQuantity}
							unitOfMeasure={unitOfMeasure}
							onChange={setLocalQuantity}
							css={styleQuantityControl}
							useUpdateButton
						/>

						<div css={priceContainer}>
							{selectedPart?.clearanceItem ?
								<>
									<PartClearancePriceEcomm
										titleSize="big"
										netPrice={selectedPart?.price ?? 0}
										price={selectedPart?.clearanceItemDiscount ?? 0}
									/>
									<PDPPriceEcomm
										hasClearanceDiscount={selectedPart?.clearanceItem}
										price={selectedPart?.clearanceItemDiscount ?? 0}
										nonDiscountedPrice={
											selectedPart?.clearanceItemDiscount ?? 0
										}
										isLoading={
											loadingInitialPart ||
											loadingChildPart ||
											loadingPartDetails
										}
									/>
								</>
							:	<PDPPriceEcomm
									price={selectedPart?.netPrice ?? ""}
									nonDiscountedPrice={selectedPart?.price}
									isLoading={
										loadingInitialPart ||
										loadingChildPart ||
										loadingPartDetails
									}
								/>
							}
						</div>

						<Box mt={2} mb={3}>
							<Button
								variant="primary-cta"
								width="162px"
								css={styleAddToCardContainer}
								iconFamily="solid"
								onClick={handleAddToCart}
								disabled={
									localQuantity < 1 ||
									loadingInitialPart ||
									loadingChildPart ||
									loadingPartDetails ||
									(initialPart?.isParent && !partDetails?.children.length)
								}
							>
								<span css={styleAddToCardLabel}>Add To Cart</span>
							</Button>
						</Box>

						{alreadyInCartTally > 0 && (
							<div>
								<Paragraph display="inline-block" mr={0.5}>
									You currently have {alreadyInCartTally} of this item in your
									cart.
								</Paragraph>
								<EventTracker
									events={[
										TrackingEvent.PDP_GO_TO_CART,
										TrackingEvent.GO_TO_CART,
									]}
									trackEvent={trackEvent}
								>
									<Link
										to="/shop/cart-summary"
										icon="shopping-cart"
										css={styleGoToCart}
									>
										Go to cart
									</Link>
								</EventTracker>
							</div>
						)}
					</div>
				</div>

				<PartDetailTabs partDetails={partDetails} initialPart={initialPart} />

				<Divider mt={4} mb={4} />

				{relatedPartIds.length > 0 && (
					<PartPreviewRow
						partIds={(relatedPartIds ?? [])
							.map((p) => p.id)
							.filter((id) => id !== partId)}
						heading="Related Products"
						headingVariant="h3"
						onAddToCart={handleAddRelatedProductToCart}
					/>
				)}

				{recentlyViewedPartIds.length > 0 && (
					<PartPreviewRow
						partIds={recentlyViewedPartIds.filter((id) => id !== partId)}
						heading="Recently Viewed"
						headingVariant="h3"
						onAddToCart={handleAddRecentlyViewProductToCart}
					/>
				)}

				<AddedToCartModalEcomm
					isOpen={showAddedToCartModal}
					partId={selectedPart?.id}
					onClose={() => setShowAddedToCartModal(false)}
				/>
			</div>
		</PageContentWrapper>
	)
}
